<div class="step-panel estamp-container">

  <!-- TOP HEADER / LOAN SUMMARY BAR -->
  <div class="estamp-header-card">
    <div class="header-main">
      <div class="header-titles">
        <div class="badge-row">
          <span class="badge b-v">STEP 9 OF 13</span>
          <span class="badge b-t">Digital Stamping · NeSL / Digio Gateway</span>
          <span class="badge b-g" *ngIf="stampProcured">✓ Certificate Procured</span>
        </div>
        <h2 class="pg-title">e-Stamp Duty Procurement</h2>
        <p class="pg-sub">
          State-mandated stamp duty under the <strong>Indian Stamp Act, 1899</strong>. Electronically procured via Digio NeSL Information Utility gateway and automatically merged as Page 1 of the digital loan contract prior to Aadhaar e-Sign.
        </p>
      </div>

      <!-- Quick Terms Metric Chips -->
      <div class="loan-terms-strip" *ngIf="sanctionedAmount > 0">
        <div class="term-pill">
          <span class="tp-lbl">Sanctioned Amount</span>
          <span class="tp-val">₹ {{ sanctionedAmount | number:'1.0-0' }}</span>
        </div>
        <div class="term-pill">
          <span class="tp-lbl">Jurisdiction State</span>
          <span class="tp-val">{{ borrowerState }} ({{ borrowerStateCode }})</span>
        </div>
        <div class="term-pill highlight">
          <span class="tp-lbl">Applicable Duty</span>
          <span class="tp-val">₹ {{ applicableDuty }}</span>
        </div>
      </div>
    </div>
  </div>

  <!-- LEGAL COMPLIANCE ALERT -->
  <div class="alert info legal-banner">
    <div class="alert-icon">📜</div>
    <div class="alert-text">
      <strong>Statutory Enforceability Requirement</strong>
      <span>
        Under Section 35 of the Indian Stamp Act, unstamped or improperly stamped agreements are inadmissible as evidence in court. Electronic stamping via NeSL ensures instant legal enforceability and Information Utility registration under the Insolvency and Bankruptcy Code (IBC).
      </span>
    </div>
  </div>

  <!-- MAIN 2-COLUMN GRID -->
  <div class="g2 estamp-main-grid">

    <!-- LEFT COLUMN: Specifications & Procurement Hub -->
    <div class="left-col">

      <!-- CARD 1: STAMP DUTY SPECIFICATIONS -->
      <div class="card">
        <div class="card-title">
          <div class="ct-bar"></div>
          <span>State Treasury &amp; Duty Assessment</span>
        </div>

        <div class="spec-grid">
          <div class="spec-item">
            <span class="spec-lbl">Borrower State (KYC)</span>
            <span class="spec-val">{{ borrowerState }}</span>
          </div>
          <div class="spec-item">
            <span class="spec-lbl">Stamp Duty Rate</span>
            <span class="spec-val text-pri">₹ {{ applicableDuty }} Fixed</span>
          </div>
          <div class="spec-item span2">
            <span class="spec-lbl">Statutory Schedule / Article</span>
            <span class="spec-val font-mono">{{ dutySchedule }} ({{ articleDescription }})</span>
          </div>
          <div class="spec-item">
            <span class="spec-lbl">Agreement Ref</span>
            <span class="spec-val font-mono">{{ agreementRef }}</span>
          </div>
          <div class="spec-item">
            <span class="spec-lbl">e-Stamping Vendor</span>
            <span class="spec-val">NeSL / State Electronic Treasury</span>
          </div>
        </div>
      </div>

      <!-- CARD 2: PARTIES TO INSTRUMENT -->
      <div class="card">
        <div class="card-title">
          <div class="ct-bar"></div>
          <span>Parties to Legal Instrument</span>
        </div>

        <div class="parties-list">
          <div class="party-item lender">
            <div class="party-role">FIRST PARTY (LENDER)</div>
            <div class="party-name">Nishwik NBFC Financial Services Ltd</div>
            <div class="party-meta">CIN: U65929TG2020PTC145892 · RBI Registered NBFC-ND</div>
          </div>

          <div class="party-item borrower">
            <div class="party-role">SECOND PARTY (BORROWER)</div>
            <div class="party-name">{{ borrowerName }}</div>
            <div class="party-meta">
              <span>PAN: {{ borrowerPan }}</span>
              <span class="dot-sep">·</span>
              <span>Aadhaar: {{ borrowerAadhaarMask }}</span>
            </div>
            <div class="party-address">📍 {{ borrowerAddress }}</div>
          </div>
        </div>
      </div>

      <!-- CARD 3: PROCUREMENT EXECUTION -->
      <div class="card action-card">
        <div class="card-title">
          <div class="ct-bar"></div>
          <span>e-Stamp Procurement Hub</span>
        </div>

        <div class="action-panel">
          <p class="action-desc">
            Click below to initiate automated e-Stamp procurement from the {{ borrowerState }} State Treasury via Digio NeSL Gateway.
          </p>

          <button class="btn btn-pri btn-lg w-100 procure-btn"
            (click)="procureStamp()"
            [disabled]="procuring || stampProcured">
            <span *ngIf="procuring" class="spinner-border spinner-border-sm me-2"></span>
            <span *ngIf="!procuring && !stampProcured">🏛️ Procure e-Stamp Certificate (₹ {{ applicableDuty }})</span>
            <span *ngIf="stampProcured">✓ e-Stamp Certificate Procured &amp; Confirmed</span>
          </button>

          <!-- Confirmed Status Banner -->
          <div class="procured-pill" *ngIf="stampProcured">
            <div class="pp-icon">✓</div>
            <div class="pp-body">
              <div class="pp-title">e-Stamp Verified &amp; Attached to Agreement</div>
              <div class="pp-meta">
                Cert: <strong>{{ certNumber }}</strong> · {{ procuredDate }} · Txn: {{ digioTxnId }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- FAILURE NOTICE -->
      <div *ngIf="stampFailed" class="exc-state err" style="margin-top:14px;">
        <div class="exc-title">Procurement Notice</div>
        <div class="exc-body">{{ errorMessage }}</div>
        <div class="exc-actions">
          <button class="btn btn-warn btn-sm" (click)="procureStamp()" [disabled]="procuring">
            {{ procuring ? 'Retrying...' : 'Retry Procurement' }}
          </button>
        </div>
      </div>

    </div>

    <!-- RIGHT COLUMN: Certificate Preview & Legal Terms -->
    <div class="right-col">

      <!-- CARD 4: GOVERNMENT e-STAMP CERTIFICATE PREVIEW -->
      <div class="card certificate-card" [class.procured]="stampProcured">
        <div class="card-title">
          <div class="ct-bar"></div>
          <span>Government e-Stamp Certificate (NeSL / SHCIL)</span>
        </div>

        <!-- Official e-Stamp Certificate Mockup -->
        <div class="cert-container">
          <div class="cert-header">
            <div class="govt-emblem">🇮🇳</div>
            <div class="govt-text">
              <div class="gh-main">GOVERNMENT OF {{ borrowerState | uppercase }}</div>
              <div class="gh-sub">e-Stamp Certificate</div>
            </div>
            <div class="nesl-logo">NeSL</div>
          </div>

          <div class="cert-body-table">
            <div class="c-row">
              <div class="c-lbl">Certificate No.</div>
              <div class="c-val font-mono cert-num">{{ certNumber || 'IN-' + borrowerStateCode + '20260901471680' }}</div>
            </div>
            <div class="c-row">
              <div class="c-lbl">Certificate Issued Date</div>
              <div class="c-val">{{ procuredDate || 'Pending Procurement' }}</div>
            </div>
            <div class="c-row">
              <div class="c-lbl">Account Reference</div>
              <div class="c-val font-mono">NONACC / (CI/ digio_app / {{ borrowerStateCode | lowercase }})</div>
            </div>
            <div class="c-row">
              <div class="c-lbl">Unique Doc. Reference</div>
              <div class="c-val font-mono">{{ digioTxnId || 'DGO-ESTP-' + (applicationId || '9921') }}</div>
            </div>
            <div class="c-row">
              <div class="c-lbl">Purchased by</div>
              <div class="c-val">{{ borrowerName }}</div>
            </div>
            <div class="c-row">
              <div class="c-lbl">Description of Document</div>
              <div class="c-val">{{ articleDescription }}</div>
            </div>
            <div class="c-row">
              <div class="c-lbl">Consideration Price (Rs.)</div>
              <div class="c-val">₹ {{ sanctionedAmount | number:'1.0-0' }} (Sanctioned Credit Facility)</div>
            </div>
            <div class="c-row">
              <div class="c-lbl">First Party</div>
              <div class="c-val">Nishwik NBFC Financial Services Ltd</div>
            </div>
            <div class="c-row">
              <div class="c-lbl">Second Party</div>
              <div class="c-val">{{ borrowerName }}</div>
            </div>
            <div class="c-row highlight-duty">
              <div class="c-lbl">Stamp Duty Amount(Rs.)</div>
              <div class="c-val duty-val">₹ {{ applicableDuty }} ({{ applicableDuty === 300 ? 'Three Hundred Only' : 'One Hundred Only' }})</div>
            </div>
          </div>

          <div class="cert-footer-note">
            🛡️ <strong>Statutory Alert:</strong> The authenticity of this e-Stamp certificate can be validated electronically via the NeSL/SHCIL verification gateway.
          </div>
        </div>
      </div>

      <!-- CARD 5: TERMS & CONDITIONS OF LEGAL STAMPING -->
      <div class="card terms-card">
        <div class="card-title">
          <div class="ct-bar"></div>
          <span>Terms &amp; Conditions · Digital Instrument Stamping</span>
        </div>

        <div class="terms-content">
          <div class="tc-item">
            <div class="tc-num">1</div>
            <div class="tc-text">
              <strong>Evidentiary Admissibility (Section 35 Indian Stamp Act):</strong>
              The digital loan agreement executed pursuant to this stamp certificate is legally admissible in Indian courts as valid evidentiary proof of the borrowing obligation.
            </div>
          </div>

          <div class="tc-item">
            <div class="tc-num">2</div>
            <div class="tc-text">
              <strong>NeSL Information Utility (IU) Registration:</strong>
              The debt instrument and financial information are recorded in the National E-Governance Services Limited (NeSL) Information Utility under the Insolvency and Bankruptcy Code (IBC) 2016.
            </div>
          </div>

          <div class="tc-item">
            <div class="tc-num">3</div>
            <div class="tc-text">
              <strong>Pre-Attachment Workflow:</strong>
              This e-Stamp certificate is cryptographically merged as Page 1 of the Loan Agreement PDF before borrower Aadhaar OTP e-Signing in Step 10.
            </div>
          </div>
        </div>
      </div>

    </div>

  </div>

  <!-- NAVIGATION BOTTOM BAR -->
  <div class="nav-bar estamp-nav">
    <button class="btn btn-sec btn-lg" (click)="prev.emit()">
      ← Back to KFS Delivery
    </button>
    <button class="btn btn-pri btn-lg next-btn"
      (click)="proceedToNext()"
      [disabled]="!stampProcured">
      Stamp Procured → Proceed to e-Agreement &amp; e-Sign (Step 10) →
    </button>
  </div>

</div>
